import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client'

import {
  ArtistPostsFragmentDoc,
  ConsolePostQuery,
  ConsolePostQueryVariables,
  DeletePost,
  DeletePostMutation,
  DeletePostMutationVariables,
  PostStatus,
  PostType,
  StoryFilter,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { data, fetchMore, refetch, networkStatus } = useQuery<
    ConsolePostQuery,
    ConsolePostQueryVariables
  >(QUERY, {
    variables: {
      artistId: user?.uid ?? '',
      status: [PostStatus.Scheduled, PostStatus.Published],
      storyFilter: StoryFilter.Include,
      pageInput: { limit: 10 }, // NOTE: utoniqPaginationのmerge処理に必要なため削除NG
      pageInputById: { limit: 10 },
    },
    notifyOnNetworkStatusChange: true,
  })
  const [deletePost, { loading: loadingDeletePost }] = useMutation<
    DeletePostMutation,
    DeletePostMutationVariables
  >(DeletePost)

  return {
    state: {
      user,
      userId: data?.user?.id || '',
      artistId: user?.uid ?? '',
      loadingDeletePost,
      artist: data?.artist ?? null,
      loading:
        networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.refetch ||
        networkStatus === NetworkStatus.fetchMore,
      hasNextPage: !!data?.artist?.posts?.pageInfo.hasNextPage,
      posts: data?.artist?.posts ?? null,
      membership: data?.artist?.subscription?.products ?? undefined,
    },
    actions: {
      async onFilter(resourceType) {
        await refetch({
          artistId: user?.uid ?? '',
          resourceType: resourceType.type as PostType,
          resourceTypes: resourceType.types,
          storyFilter: resourceType.storyFilter,
          status: [PostStatus.Scheduled, PostStatus.Published],
        })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            artistId: user?.uid ?? '',
            status: [PostStatus.Scheduled, PostStatus.Published],
            // NOTE: utoniqPaginationのmerge処理に必要なため削除NG
            pageInput: {
              cursor: data?.artist?.posts?.pageInfo.endCursor || null,
            },
            pageInputById: {
              cursor: data?.artist?.posts?.pageInfo.endCursor || null,
            },
          },
        })
      },
      async onDeletePost(postId: string) {
        await deletePost({ variables: { postId: postId } })
      },
    },
  }
})
const QUERY = gql`
  query ConsolePost(
    $artistId: ID!
    $pageInput: PageInputDate
    $pageInputById: PageInput
    $resourceType: PostType
    $resourceTypes: [PostType!]
    $storyFilter: StoryFilter
    $status: [PostStatus]
  ) {
    artist(id: $artistId) {
      id
      paymentType
      hasArrangingDeliveryItems
      subscription {
        products {
          productId
        }
      }
      posts(
        pageInput: $pageInput
        pageInputById: $pageInputById
        resourceType: $resourceType
        resourceTypes: $resourceTypes
        storyFilter: $storyFilter
        status: $status
      ) {
        ...ArtistPosts
      }
    }
    user {
      id
      accountId
      formattedAccountId @client
    }
  }
  ${ArtistPostsFragmentDoc}
`
